/**
 * Paleta de la piel "Marca" para el lado JS (el tema de AntD).
 *
 * <b>Por qué existe:</b> AntD se configura con un objeto de tokens en JS, y el
 * resto de la app se pinta con custom properties de CSS. Los mismos cinco
 * colores semánticos vivían escritos dos veces —literales hex en App.tsx y
 * `--color-*` en index.css— sin nada que los atara: cambiar uno y olvidar el
 * otro deja los componentes de AntD y el CSS propio con marcas distintas, que es
 * el tipo de desajuste que nadie ve en el diff y todos ven en pantalla.
 *
 * <b>Por qué no genera el CSS:</b> hacerlo exigiría un paso de build. En su lugar
 * index.css sigue siendo la fuente del lado CSS y `scripts/check-palette.js`
 * —que corre dentro de `npm run lint`, y por lo tanto en CI— falla si los dos
 * lados dejan de coincidir. Un valor no puede cambiar en un lado en silencio.
 *
 * Nota: el KDS no participa. Tiene su propia paleta `--kds-*` en kitchen.css
 * porque es una superficie siempre oscura, ajena al toggle de tema.
 */
export interface Palette {
  primary: string;
  success: string;
  error: string;
  warning: string;
  info: string;
  /** Fondo del Layout de AntD. Sólo JS: no tiene contraparte en index.css. */
  bgLayout: string;
}

export const LIGHT_PALETTE: Palette = {
  primary: '#128078',
  success: '#4c8f28',
  error: '#cf4437',
  warning: '#d9a012',
  info: '#2879a4',
  bgLayout: '#eef4f3',
};

export const DARK_PALETTE: Palette = {
  primary: '#1a9a90',
  success: '#6fbf46',
  error: '#e0685c',
  warning: '#e3b83a',
  info: '#55a4cf',
  bgLayout: '#0f1716',
};

export const getPalette = (isDark: boolean): Palette =>
  isDark ? DARK_PALETTE : LIGHT_PALETTE;

/**
 * Misma familia que declara `body` en index.css. Va aquí además de allá porque
 * AntD la necesita como token para sus portales (modales, dropdowns), que se
 * montan fuera del árbol donde `body` hereda.
 */
export const FONT_FAMILY =
  "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";
